<template>
  <sidebar-row :border-bottom="isVisible">
    <v-row no-gutters class="align-center mb-2 font-weight-medium" style="gap: 0.3rem; flex-wrap: nowrap">
      <v-col cols="auto" class="d-flex align-center">
        <v-icon :class="{ 'is-expanded': isVisible }" @click="isVisible = !isVisible">mdi-menu-right</v-icon>
      </v-col>
      <v-col cols="auto" class="d-flex align-center">
        <v-icon size="20" color="rgba(0, 0, 0, 0.7)">
          {{ datasourceIcon }}
        </v-icon>
      </v-col>
      <v-col cols="auto" class="d-block db-name">
        {{ datasourceMetadata.verbose_name ?? '' }}
      </v-col>
      <v-col cols="auto">({{ relations.length }})</v-col>
      <v-col v-if="!datasourceMetadata.geometry_type" cols="auto" class="d-flex align-center ml-2">
        <v-tooltip bottom color="font">
          <template #activator="{ on }">
            <v-btn v-on="on" @click="openAddDialog" color="success" x-small height="25" width="25" fab elevation="0">
              <v-icon>mdi-plus</v-icon>
            </v-btn>
          </template>
          {{ $i18n.t('tooltips.addNewObject') }}
        </v-tooltip>
      </v-col>
      <v-col
        v-if="
          isUldkToolsModuleConfigured &&
            (!Object.keys(uldkToolsAttributesMappings).length ||
              uldkToolsAttributesMappings[datasourceMetadata.name]) &&
            ['polygon', 'multipolygon'].includes(datasourceMetadata.geometry_type)
        "
        cols="auto"
        class="d-flex align-center ml-2"
      >
        <assign-parcel-trigger
          @customAction="openAddDialog"
          :layer="selectedLayerId"
          :datasource="datasourceMetadata.name"
          forced-provider="uldk"
          :geometry-type="datasourceMetadata.geometry_type"
        >
          <template #default="{ item: itemButton, defaultAction }">
            <v-tooltip v-if="uldkToolsAttributesMappings[datasourceMetadata.name]" bottom color="font">
              <template #activator="{ on }">
                <v-btn
                  v-on="on"
                  color="success"
                  x-small
                  height="25"
                  width="25"
                  fab
                  elevation="0"
                  :disabled="itemButton.disabled"
                  :loading="itemButton.loading"
                  @click="defaultAction"
                >
                  <v-icon style="font-size: 15px">{{ itemButton.icon }}</v-icon>
                </v-btn>
              </template>
              {{ itemButton.tooltip }}
            </v-tooltip>
          </template>
        </assign-parcel-trigger>
      </v-col>
    </v-row>
    <v-row no-gutters v-if="isVisible">
      <v-col>
        <v-data-table
          :headers="headers"
          :items="relations"
          v-on="listeners"
          :item-style="dataTableRowStyle"
          disable-pagination
          hide-default-footer
          dense
        >
          <template #[`item.description`]="{ item }">
            <v-row no-gutters align="center" class="text-body-2">
              <v-col
                cols="auto"
                v-for="(desc, idx) of item.description"
                :key="idx"
                class="desc-badge"
                :style="
                  `${desc.color ? `background-color: ${desc.color}` : ''}; color: ${
                    $_isHexColorLight(desc.color || '#ffffff') ? '#202124' : 'white'
                  }`
                "
              >
                {{ desc.text }}
              </v-col>
            </v-row>
          </template>
          <template #[`item.options`]="{ item }">
            <v-row no-gutters class="justify-end align-center" style="gap: 0.2rem; flex-wrap: nowrap">
              <v-col
                v-for="button in getActionButtons(item)"
                :key="button.name"
                cols="auto"
                class="d-flex align-center"
              >
                <v-tooltip bottom color="font" content-class="tooltip_multiline">
                  <template #activator="{ on }">
                    <span v-on="on">
                      <v-icon v-on="button.listeners" size="18" :disabled="button.disabled">
                        {{ button.icon }}
                      </v-icon>
                    </span>
                  </template>
                  <span>{{ button.tooltip }}</span>
                </v-tooltip>
              </v-col>
              <v-col cols="auto" class="d-flex align-center">
                <dots-menu :items="getDotsItems(item)" @deleteRelation="openDeleteDialog(item)" font-size="18px" />
              </v-col>
            </v-row>
          </template>
        </v-data-table>
      </v-col>
    </v-row>
    <main-input-dialog
      ref="select-layer-dialog"
      v-if="isLayerSelectionDialogLoaded"
      :is-visible.sync="isLayerSelectionDialogVisible"
      :title-translation="$i18n.t('dialog.title.selectLayer')"
      @agree="handleSelectLayer"
      :inputs="selectLayerInputs"
      agree-text="dialog.agreeChoose"
      disable-default-form-bottom-padding
    />
    <add-edit-feature-dialog
      v-if="isAddFeatureDialogLoaded"
      :is-visible.sync="isAddFeatureDialogVisible"
      v-bind="addEditData"
      @done="handleAddNewRelationFeature"
    >
      <template #prepend_form>
        <v-alert
          icon="mdi-information-outline"
          color="primary"
          style="font-size: 12px"
          class="text-left my-5"
          dense
          text
        >
          {{
            $i18n.t('dialog.body.addRelationFeatureInfo', {
              datasourceName: mainDatasourceVerboseName ?? '',
              descAttribute: descAttributeValue,
            })
          }}
        </v-alert>
      </template>
    </add-edit-feature-dialog>
    <main-dialog
      v-if="isDialogLoaded"
      :is-visible.sync="isDialogVisible"
      :title="$i18n.t('dialog.title.deleteRelation')"
      :body="$i18n.t('dialog.body.deleteRelationConfirmation')"
      @agree="handleRelationDelete"
      :loading="isDialogLoading"
      agree-text="dialog.agreeDelete"
    />
  </sidebar-row>
</template>
<script>
import { call, get } from 'vuex-pathify';
import DotsMenu from '@/components/DotsMenu';
import MainInputDialog from '@/components/MainInputDialog';
import SidebarRow from '@/components/SidebarRow';
import stylePreviewUtilities from '@/mixins/stylePreviewUtilities';
import AddEditFeatureDialog from '@/components/AddEditFeatureDialog.vue';
import AssignParcelTrigger from '@/components/AssignParcelTrigger';
import { isString } from 'lodash';

export default {
  name: 'TheRelation',
  mixins: [stylePreviewUtilities],
  components: {
    DotsMenu,
    MainDialog: () => import('@/components/MainDialog'),
    SidebarRow,
    AddEditFeatureDialog,
    MainInputDialog,
    AssignParcelTrigger,
  },
  props: {
    featureId: {
      type: [String, Number],
      required: true,
    },
    dataSourceName: {
      type: String,
      required: true,
    },
    mainDatasourceName: {
      type: String,
      required: true,
    },
    descAttributeValue: {
      type: String,
    },
    relations: {
      type: Array,
      required: true,
    },
    hoverDisabled: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    isUldkToolsEnabled: get('admin/settingsValues@uldk_tools_module_enabled'),
    uldkToolsMetadata: get('admin/additionalModules@ULDK_TOOLS_MODULE'),
    isUldkToolsModuleConfigured() {
      return this.uldkToolsMetadata?.enabled && this.uldkToolsMetadata?.configured && this.isUldkToolsEnabled;
    },
    projectElements: get('layers/project@layers'),
    datasourceMetadata: get('layers/metadata@:dataSourceName'),
    mainDatasourceVerboseName: get('layers/metadata@:mainDatasourceName.verbose_name'),
    uldkToolsAttributesMappings: get('search/uldkToolsAttributesMappings'),
    layers: get('layers/layers'),
    projectLayers() {
      return this.$_getFlatGroupsLayers(this.projectElements).layers;
    },
    users: get('users/users'),
    headers() {
      return [
        {
          text: this.$i18n.t('default.feature'),
          align: 'start',
          value: 'description',
          sort: (a, b) => {
            const aText = a[0]?.text;
            const bText = b[0]?.text;
            return isString(aText) ? aText.localeCompare(bText) : aText - bText;
          },
        },
        {
          text: '',
          align: 'end',
          sortable: false,
          value: 'options',
        },
      ];
    },
    datasourceIcon() {
      return this.getDataSourceIcon(this.datasourceMetadata.geometry_type || this.datasourceMetadata.service_type);
    },
    selectLayerInputs() {
      return [
        {
          dataType: {
            name: 'select',
          },
          name: 'layerId',
          itemText: 'name',
          cols: '12',
          itemValue: 'id',
          translationPath: 'default.layer',
          subtitle: 'dialog.body.addFeatureLayerSubtitle',
          items: this.layersToSelect,
          rules: [v => !!v || 'rules.required'],
        },
      ];
    },
    listeners() {
      const clickRowHandler = {
        'click:row': (_, { item }) => (this.getPushCardDisabled(item) ? () => {} : this.openFeatureCard(item)),
      };
      return this.hoverDisabled
        ? { ...clickRowHandler }
        : {
            'mouseleave:row': (_, { item }) => this.onHover(false, item),
            'mouseenter:row': (_, { item }) => this.onHover(true, item),
            ...clickRowHandler,
          };
    },
  },
  data: () => ({
    isDialogLoaded: false,
    isDialogLoading: false,
    isDialogVisible: false,
    isVisible: true,
    isAddFeatureDialogLoaded: false,
    isAddFeatureDialogVisible: false,
    isLayerSelectionDialogVisible: false,
    isLayerSelectionDialogLoaded: false,
    layersToSelect: [],
    addEditData: {},
    deleteData: null,
    selectedLayerId: null,
  }),
  methods: {
    deleteRelation: call('relations/deleteRelation'),
    getLayerFeature: call('layers/getLayerFeature'),
    addRelation: call('relations/addRelation'),
    getDotsItems() {
      return [{ name: 'deleteRelation' }];
    },
    dataTableRowStyle(item) {
      return this.getPushCardDisabled(item) ? 'cursor: default' : 'cursor: pointer';
    },
    getActionButtons(item) {
      return [
        ...(item.geometry
          ? [
              {
                name: 'zoom',
                icon: 'mdi-magnify',
                tooltip: this.$i18n.t('tooltips.zoomToFeature'),
                listeners: {
                  click: () => this.zoomToFeature(item),
                },
              },
            ]
          : []),
        {
          name: 'help',
          icon: 'mdi-help-circle',
          tooltip: this.getInfo(item),
        },
      ];
    },
    getPushCardDisabled(item) {
      const isLayerNotInProject =
        this.projectLayers.findIndex(layer => layer.data_source_name === item.data_source_name) < 0;
      return isLayerNotInProject;
    },
    refreshRelations() {
      this.$root.$emit('refreshTableData');
      this.$root.$emit('refreshFormData');
      const layersIds = this.projectLayers
        .filter(layer => layer.data_source_name === this.dataSourceName)
        .map(layer => layer.id);
      if (layersIds?.length) {
        this.$root.$emit(
          'refreshMvtSources',
          layersIds.map(layer => ({ id: layer, group: 'layers' })),
          true
        );
        this.$root.$emit('updateLegendStylesCounts', { layersIds });
      }
    },
    async handleRelationDelete() {
      this.isDialogLoading = true;
      try {
        const payload = {
          data_source_name: this.mainDatasourceName,
          feature_id: this.featureId,
          features: [this.deleteData],
        };
        await this.deleteRelation(payload);
        this.refreshRelations();
        this.isDialogLoading = false;
        this.isDialogVisible = false;
        this.deleteData = null;
      } catch {
        this.isDialogLoading = false;
      }
    },
    handleSelectLayer() {
      this.selectedLayerId = this.$refs['select-layer-dialog']?.inputs[0].value;
      this.isLayerSelectionDialogVisible = false;
      this.openAddFeatureDialog(this.selectedLayerId);
    },
    getInfo(item) {
      const { assigned_at, assigned_by } = item;
      const date = this.$dayjs(assigned_at);
      const dateTime = `${date.format('L')}, ${date.format('LT')}`;
      const userName = this.users[assigned_by]?.name;
      return Object.entries({
        ...(userName && { author: userName }),
        date: dateTime,
      }).reduce(
        (acc, [key, value]) => `${acc}${acc ? '\n' : ''}${this.$i18n.t(`tooltips.relation_${key}`)}: ${value}`,
        ''
      );
    },
    getName(relation) {
      const { feature_id, feature_desc_attribute_value } = relation;
      const attributeValue =
        feature_desc_attribute_value || feature_desc_attribute_value === 0 || feature_desc_attribute_value === null
          ? feature_desc_attribute_value
          : feature_id;
      return attributeValue;
    },
    onHover(value, item) {
      this.$root.$emit('toggleFeatureHover', item, value);
    },
    openDeleteDialog(item) {
      this.deleteData = item;
      this.isDialogLoaded = this.isDialogVisible = true;
    },
    async openFeatureCard(item) {
      const { data_source_name, feature_id } = item;
      const layersIds = this.projectLayers
        .filter(layer => layer.data_source_name === data_source_name)
        .map(layer => layer.id);
      for (const layerId of layersIds) {
        try {
          const r = await this.getLayerFeature({
            layer_id: layerId,
            feature_id: feature_id,
            skipDefaultErrorHandler: true,
          });
          if (r?.id == feature_id) {
            this.$router.push({
              name: 'layerFeature',
              params: { lid: layerId, fid: feature_id },
            });
            return;
          }
        } catch {
          //
        }
      }
      this.$store.set('snackbar/PUSH_MESSAGE!', {
        message: this.$i18n.t('snackbar.noLayerInProjectToPush'),
      });
    },
    async handleAddNewRelationFeature(closeDialog, metadata, newItemId) {
      try {
        await this.addRelation({
          data_source_name: this.mainDatasourceName,
          feature_id: this.featureId,
          features: [
            {
              data_source_name: metadata.datasource,
              feature_id: newItemId,
            },
          ],
        });
        this.refreshRelations();
        this.selectedLayerId = null;
        this.addEditData = {};
        this.$root.$emit('deleteSidebarGeometry');
        closeDialog();
      } catch {
        //
      }
    },
    openAddFeatureDialog(layerId) {
      this.addEditData = {
        ...this.addEditData,
        title: this.$i18n.t('dialog.title.addNewObjectName', { layer_name: this.layers[layerId].name }),
        featureData: {
          datasource: this.dataSourceName,
          layer: layerId,
        },
      };
      this.isAddFeatureDialogVisible = this.isAddFeatureDialogLoaded = true;
    },
    openLayerSelectionDialog(layers) {
      this.layersToSelect = layers;
      this.isLayerSelectionDialogVisible = this.isLayerSelectionDialogLoaded = true;
    },
    openAddDialog({ values, geometry }) {
      this.selectedLayerId = null;
      this.addEditData = {};
      const foundLayers = Object.values(this.layers).filter(layer => layer.data_source_name === this.dataSourceName);
      if (!foundLayers.length) return;
      this.addEditData = {
        featureMetadata: {
          properties: values,
        },
        geometry,
      };
      if (foundLayers.length === 1) {
        this.selectedLayerId = foundLayers[0].id;
        this.openAddFeatureDialog(this.selectedLayerId);
      } else {
        this.openLayerSelectionDialog(foundLayers);
      }
    },
    zoomToFeature(item) {
      const geometry = JSON.parse(JSON.stringify(item.geometry));
      geometry.crs = { properties: { name: this.$_config.defaultEpsg }, type: 'name' };
      this.$root.$emit('createGeomAndFitView', geometry);
    },
  },
  mounted() {
    this.$watch(
      vm => ({ isAddVisible: vm.isAddFeatureDialogVisible, isLayerSelectionVisible: vm.isLayerSelectionDialogVisible }),
      nV => {
        if (!nV.isAddVisible || !nV.isLayerSelectionVisible) this.$root.$emit('deleteSidebarGeometry');
      }
    );
  },
};
</script>

<style lang="scss" scoped>
.is-expanded {
  transform: rotate(90deg);
}
.db-name {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex-wrap: nowrap;
  flex-shrink: 1;
}
:deep(.v-icon::after) {
  background-color: rgba($color: #fff, $alpha: 0);
}
.desc-badge {
  font-size: 12px;
  font-weight: 500;
  padding: 1px 8px 1px 0px !important;
  border-radius: 4px !important;
  font-weight: 500px !important;
  &:not(:first-child) {
    padding: 1px 8px 1px 8px !important;
  }
}
</style>
